import { useState, useEffect } from 'react';
import { useAppContext } from '@/App';
import { getCourses, getCourseTerms } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useNavigate } from 'react-router-dom';
import { Search, BookOpen, Users, ArrowRight, Calendar, X } from 'lucide-react';

export default function CourseCatalog() {
  const { currentStudent } = useAppContext();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [termFilter, setTermFilter] = useState('all');
  const [terms, setTerms] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      setLoading(true);
      // `catalog` asks for the college-wide published list rather than the
      // caller's own enrolled courses — this page is the browse view.
      const [res, tRes] = await Promise.all([getCourses({ catalog: true }), getCourseTerms()]);
      setCourses(res.data);
      setTerms(tRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const searchQuery = search.trim().toLowerCase();
  const hasSearch = Boolean(searchQuery);
  const hasActiveFilters = hasSearch || categoryFilter !== 'all' || typeFilter !== 'all' || termFilter !== 'all';
  const filtered = courses.filter(c => {
    const title = String(c?.title || '').toLowerCase();
    const courseCode = String(c?.course_code || '').toLowerCase();
    if (searchQuery && !title.includes(searchQuery) && !courseCode.includes(searchQuery)) return false;
    if (categoryFilter !== 'all' && c.category !== categoryFilter) return false;
    if (typeFilter !== 'all' && c.type !== typeFilter) return false;
    if (termFilter !== 'all' && (c.term || '') !== termFilter) return false;
    return true;
  });

  const categories = [...new Set(courses.map(c => c.category))];
  const isEnrolled = (course) => course.students_enrolled?.includes(currentStudent?.id);

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Course Catalog</h2>
        <p className="text-sm text-[#062B1A]/60 mt-1">
          Browse the courses offered this year. Registration is handled by the registrar — contact
          the college to add a course to your program.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3" data-testid="course-catalog-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#062B1A]/40" />
          <Input
            type="search"
            aria-label="Search courses"
            placeholder="Search courses..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-9 border-[rgba(6, 43, 26,0.12)] focus-visible:ring-[#D4AF37]/40 [&::-webkit-search-cancel-button]:appearance-none"
            data-testid="course-catalog-search-input"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Clear course search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-[#062B1A]/40 hover:bg-[#062B1A]/5 hover:text-[#062B1A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]/50"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[160px] border-[rgba(6, 43, 26,0.12)]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[140px] border-[rgba(6, 43, 26,0.12)]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="self-paced">Self-Paced</SelectItem>
            <SelectItem value="live">Live</SelectItem>
            <SelectItem value="both">Both</SelectItem>
          </SelectContent>
        </Select>
        <Select value={termFilter} onValueChange={setTermFilter}>
          <SelectTrigger className="w-[160px] border-[rgba(6, 43, 26,0.12)]">
            <SelectValue placeholder="Term" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Terms</SelectItem>
            {terms.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Course Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-64 bg-white rounded-xl animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <BookOpen className="w-12 h-12 text-[#062B1A]/15 mx-auto mb-3" />
          <p className="text-[#062B1A]/50">
            {hasSearch ? `No courses match “${search.trim()}”.` : hasActiveFilters ? 'No courses match the current filters.' : 'No courses found'}
          </p>
          {hasSearch && (
            <Button type="button" variant="outline" size="sm" className="mt-4" onClick={() => setSearch('')}>
              Clear search
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(course => (
            <Card
              key={course.id}
              className="card-hover border border-[rgba(6, 43, 26,0.08)] shadow-sm cursor-pointer group overflow-hidden"
              onClick={() => navigate(`/student/courses/${course.id}`)}
              data-testid={`course-card-${course.id}`}
            >
              {/* Thumbnail */}
              <div className="h-36 bg-gradient-to-br from-[#062B1A] to-[#0A3B24] flex items-center justify-center relative">
                <BookOpen className="w-10 h-10 text-[#D4AF37]/60" />
                <Badge className="absolute top-3 right-3 text-[10px] bg-white/20 text-white border-0">{course.type}</Badge>
              </div>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge variant="outline" className="text-[10px] border-[#D4AF37]/30 text-[#D4AF37]">{course.category}</Badge>
                  {course.term && <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700 bg-blue-50"><Calendar className="w-3 h-3 mr-1" />{course.term}</Badge>}
                  {course.course_code && <Badge variant="outline" className="text-[10px] border-[#062B1A]/20 text-[#062B1A]/60">{course.course_code}</Badge>}
                </div>
                <h3 className="text-sm font-semibold text-[#062B1A] line-clamp-2 mb-1" style={{ fontFamily: 'var(--font-display)' }}>{course.title}</h3>
                <p className="text-xs text-[#062B1A]/50 line-clamp-2 mb-3">{course.description}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-xs text-[#062B1A]/40">
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" />{course.enrollment_count}</span>
                    <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" />{course.lessons_count} lessons</span>
                  </div>
                  {/* Registration is registrar-controlled: the catalog is for
                      browsing, and enrolled courses link through to the work. */}
                  {isEnrolled(course) ? (
                    <Button
                      size="sm"
                      className="btn-hover h-7 bg-[#062B1A] text-xs text-white hover:bg-[#041B10]"
                      onClick={() => navigate(`/student/courses/${course.id}`)}
                    >
                      Open <ArrowRight className="ml-1 h-3 w-3" />
                    </Button>
                  ) : (
                    <span className="text-[11px] text-[#062B1A]/40">Not enrolled</span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
